import type { InternalLinkTarget } from '@/content/types';

/**
 * Centralized URL routing for Sanity document types.
 * Use this to generate URLs for any document reference.
 */

export type SanityDocumentType =
    | 'article'
    | 'caseStudy'
    | 'page'
    | 'landing'
    | 'industry'
    | 'partner'
    | 'service'
    | 'solution'
    | 'whitepaper';

const DOCUMENT_TYPE_ROUTES: Record<SanityDocumentType, string> = {
    article: '/insights/article',
    caseStudy: '/work',
    page: '',
    landing: '/landing',
    industry: '/industries',
    partner: '/partners',
    service: '/services',
    solution: '/solutions',
    whitepaper: '/insights/whitepaper',
};

// Document types that use a simple /{slug} URL pattern (no prefix)
const SIMPLE_SLUG_TYPES = ['industryListing', 'partnerListing', 'workListing', 'serviceListing', 'insightListing', 'page'];

// Listing/overview singletons that ARE real editor-published pages (e.g. /services).
// Distinct from global config docs (settings/organization/siteDefaults/redirect),
// which are not pages and must not trigger publish notifications.
const LISTING_DOCUMENT_TYPES = ['serviceListing', 'workListing', 'industryListing', 'partnerListing', 'insightListing'];

/**
 * Get the base URL path for a document type.
 */
export function getBaseUrl(documentType: string): string {
    // Own keys only: `in`/indexing would also match Object.prototype names (toString, ...)
    return Object.hasOwn(DOCUMENT_TYPE_ROUTES, documentType)
        ? DOCUMENT_TYPE_ROUTES[documentType as SanityDocumentType]
        : '';
}

/**
 * Whether a document type resolves to its own page route — slug-based content
 * types AND listing/overview pages (e.g. /services). Excludes global config
 * documents (settings/organization/siteDefaults/redirect), which are not pages.
 */
export function isRoutableDocumentType(documentType: string): boolean {
    return Object.hasOwn(DOCUMENT_TYPE_ROUTES, documentType) || LISTING_DOCUMENT_TYPES.includes(documentType)
}

/**
 * Whether a document type is rendered at a slug URL (`/{slug}` for pages,
 * `/{prefix}/{slug}` for everything else). Excludes listing singletons and
 * global config documents.
 */
export function hasSlugRoute(documentType: string): documentType is SanityDocumentType {
    return Object.hasOwn(DOCUMENT_TYPE_ROUTES, documentType);
}

/**
 * Generate full URL for a document given its type and slug.
 */
export function getDocumentUrl(documentType: string, slug?: string): string {
    const baseUrl = getBaseUrl(documentType);
    if (!slug) return baseUrl || '/';
    return `${baseUrl}/${slug}`;
}

/**
 * Fixed precedence for resolving a bare `/{slug}` that more than one document
 * type claims (e.g. `clearpath-cte` is both a `solution` and a `landing`).
 * Earlier entries win. `page` comes first because the root route renders
 * standard pages itself; every other type has its own prefixed route.
 */
export const ROOT_SLUG_TYPE_PRECEDENCE: readonly SanityDocumentType[] = [
    'page',
    'solution',
    'service',
    'caseStudy',
    'industry',
    'partner',
    'article',
    'whitepaper',
    'landing',
];

/**
 * Where a bare `/{slug}` should permanently redirect, given the document types
 * that own that slug. Returns null when a standard page owns it (render it at
 * the root) or when no routable document does (404).
 */
export function getRootSlugRedirectPath(slug: string, types: readonly string[]): string | null {
    const winner = ROOT_SLUG_TYPE_PRECEDENCE.find((type) => types.includes(type));
    if (!winner || winner === 'page') return null;
    return getDocumentUrl(winner, slug);
}

/**
 * Get human-readable label for a document type.
 */
export function getDocumentTypeLabel(documentType: string): string {
    const labels: Record<string, string> = {
        article: 'Article',
        caseStudy: 'Case Study',
        page: 'Page',
        landing: 'Landing Page',
        industry: 'Industry',
        partner: 'Partner',
        service: 'Service',
        solution: 'Solution',
        whitepaper: 'Whitepaper',
        serviceListing: 'Services',
        workListing: 'Work',
        industryListing: 'Industries',
        partnerListing: 'Partners',
        insightListing: 'Insights',
    };
    return labels[documentType] ?? documentType;
}

/**
 * Resolve a document type and slug to a URL path.
 * For listing pages and standard pages, returns /{slug}.
 * For content types, uses the routing map to generate the correct path.
 * Takes plain strings for callers that hold untyped records.
 */
export function resolveSlugLink(type: string, slug: string): string {
    // Use simple slug for listing pages and standard pages
    if (SIMPLE_SLUG_TYPES.includes(type)) {
        return `/${slug}`;
    }

    // Use routing utility for content types
    return getDocumentUrl(type, slug);
}

/**
 * Resolve internal link to full URL path.
 */
export function resolveInternalLink(internalLink?: InternalLinkTarget | null): string {
    if (!internalLink?.slug?.current) return '#';

    return resolveSlugLink(internalLink._type, internalLink.slug.current);
}
